import { useEffect, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Archive, ChevronRight, User } from 'lucide-react';

export default function AdminWorArchivePage() {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState('');

  const load = async (w) => {
    try {
      const url = w ? `/admin/wor_archive?week=${w}` : '/admin/wor_archive';
      const r = await api.get(url);
      setData(r.data);
      if (!week && r.data.weeks && r.data.weeks.length) setWeek(r.data.weeks[0]);
    } catch (e) { toast.error(parseErr(e)); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { if (week) load(week); }, [week]); // eslint-disable-line

  return (
    <AppShell breadcrumbs={[{ label: 'Admin' }, { label: 'WOR Archive' }]}>
      <div className="p-6 max-w-[1200px] mx-auto space-y-4 fade-up" data-testid="wor-archive-page">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <Archive size={22} />WOR Archive (read-only)
          </h1>
          <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1 max-w-[720px]">
            {data?.note || 'WOR was retired as an active workflow in Operating Rhythm v4. This archive is read-only, kept for memory and audit.'}
          </p>
        </div>

        {data && (
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
            <aside className="card p-3" data-testid="wor-weeks">
              <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold mb-1">Weeks</div>
              {(data.weeks || []).length === 0 && <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">No historical weeks.</div>}
              {(data.weeks || []).map(w => (
                <button key={w} onClick={() => setWeek(w)}
                        data-testid={`wor-week-${w}`}
                        className={`w-full text-left px-2 py-1.5 rounded text-[13px] flex items-center justify-between hover:bg-[hsl(var(--surface-2))] ${week === w ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]' : ''}`}>
                  <span>{w}</span>
                  <ChevronRight size={12} />
                </button>
              ))}
            </aside>
            <main className="space-y-2">
              <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">
                {(data.reports || []).length} report(s) for w/o <b>{week || '—'}</b>
              </div>
              {(data.reports || []).filter(r => !week || r.week_start === week).map(r => (
                <div key={r.id || `${r.user_id}-${r.week_start}`} className="card p-3" data-testid={`wor-report-${r.user_id}`}>
                  <div className="flex items-center gap-2">
                    <User size={14} className="text-[hsl(var(--foreground-muted))]" />
                    <div className="font-medium">{r.user_name || r.user_email}</div>
                    <span className={`chip ${r.status === 'submitted' ? 'chip-success' : r.status === 'draft' ? 'chip-warning' : 'chip-danger'}`}>{r.status || 'missing'}</span>
                    {r.submitted_at && (
                      <span className="text-[11.5px] text-[hsl(var(--foreground-subtle))] ml-auto">
                        Submitted {new Date(r.submitted_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      </span>
                    )}
                  </div>
                  {(r.outcomes || []).length > 0 && (
                    <div className="mt-2">
                      <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Outcomes</div>
                      <ul className="list-disc list-inside text-[12.5px] mt-1">
                        {r.outcomes.map((o, i) => (
                          <li key={i}>
                            <span className="chip mr-1">{o.category}</span>
                            {o.title}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {r.blockers && <div className="text-[12.5px] mt-1"><b>Blockers:</b> {r.blockers}</div>}
                  {r.next_week_plan && <div className="text-[12.5px] mt-1"><b>Next week:</b> {r.next_week_plan}</div>}
                </div>
              ))}
            </main>
          </div>
        )}
      </div>
    </AppShell>
  );
}
